import React from 'react';
import { Users, Target, Zap } from "lucide-react";

// ============================================================
// ABOUT US — React component for Text2SQL · Uplatz AI
// Using existing project colors and fonts
// ============================================================

const AboutUs: React.FC = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-slate-900 text-white py-20 text-center relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-1/2 -right-1/4 w-[800px] h-[800px] rounded-full bg-gradient-to-br from-indigo-500/20 to-purple-500/20 blur-3xl" />
        </div>
        <div className="relative max-w-4xl mx-auto px-6">
          <h1 className="text-4xl md:text-5xl font-extrabold mb-6">About Text2SQL</h1>
          <p className="text-lg md:text-xl text-slate-300 leading-relaxed max-w-3xl mx-auto">
            Text2SQL lets anyone ask a database a question in plain English and get working SQL back.
            We built it so non-technical teammates can answer their own data questions instead of queuing for an analyst.
          </p>
        </div>
      </section>

      {/* What Text2SQL does */}
      <section className="py-20 max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid md:grid-cols-2 gap-16 items-center">
          <div>
            <h2 className="text-3xl font-bold text-slate-900 mb-6">What Text2SQL does</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              Text2SQL translates plain English questions into accurate SQL queries for your database schema. 
              It's designed for product managers, analysts, and anyone who needs data but doesn't write SQL.
            </p>
            <p className="text-slate-600 leading-relaxed">
              It eliminates the bottleneck of waiting for a data team to answer every ad-hoc question. 
              We built it because data-driven decisions should not depend on who knows how to write a JOIN.
            </p>
          </div>
          <div className="grid grid-cols-1 gap-8">
            <div className="bg-slate-50 p-6 rounded-2xl border border-slate-100 flex items-start">
              <Target className="w-8 h-8 text-indigo-600 mr-4 flex-shrink-0" />
              <div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">Our Mission</h3>
                <p className="text-slate-600">To make database querying as simple as having a conversation, eliminating technical barriers to data analysis.</p>
              </div>
            </div>
            <div className="bg-slate-50 p-6 rounded-2xl border border-slate-100 flex items-start">
              <Users className="w-8 h-8 text-purple-600 mr-4 flex-shrink-0" />
              <div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">Who We Serve</h3>
                <p className="text-slate-600">Product managers, marketers, analysts, and anyone who needs to interact with databases to do their best work.</p>
              </div>
            </div>
            <div className="bg-slate-50 p-6 rounded-2xl border border-slate-100 flex items-start">
              <Zap className="w-8 h-8 text-blue-600 mr-4 flex-shrink-0" />
              <div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">Innovation Driven</h3>
                <p className="text-slate-600">We continually leverage the latest in AI and machine learning to improve the accuracy and efficiency of our translation engine.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Who's behind it */}
      <section className="bg-slate-50 py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-slate-900 mb-6 text-center">Who's behind it</h2>
            <p className="text-slate-600 leading-relaxed mb-4 text-center">
              Text2SQL is built and maintained by the team at Uplatz, trading as Pazit Infotech — a group with more than 10 years of experience building and running technology businesses, delivering from the UK and India.
            </p>
            <p className="text-slate-600 leading-relaxed mb-4 text-center">
              Our founding team are alumni of the University of Manchester and the IITs (Indian Institutes of Technology) — combining global business leadership with deep engineering expertise.
            </p>
            <p className="text-slate-500 leading-relaxed text-center">
              We've spent that decade shipping real software for real users, not demos. Uplatz AI is where we apply that experience to a growing suite of AI tools that solve specific, practical problems.
            </p>
          </div>
        </div>
      </section>

      {/* What we believe */}
      <section className="py-20 max-w-7xl mx-auto px-6 lg:px-8">
        <h2 className="text-3xl font-bold text-slate-900 mb-12 text-center">What we believe</h2>
        <div className="grid md:grid-cols-3 gap-8">
          <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
            <div className="text-indigo-600 font-mono text-sm font-bold tracking-wider mb-3">USEFUL &gt; FLASHY</div>
            <h3 className="text-xl font-bold text-slate-900 mb-3">Production, not demos</h3>
            <p className="text-slate-600">Every tool is built to be used today on real work, not to impress in a screenshot.</p>
          </div>
          <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
            <div className="text-purple-600 font-mono text-sm font-bold tracking-wider mb-3">ACCESSIBLE</div>
            <h3 className="text-xl font-bold text-slate-900 mb-3">No setup tax</h3>
            <p className="text-slate-600">Tools run in your browser. No infrastructure to stand up, no steep onboarding.</p>
          </div>
          <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
            <div className="text-blue-600 font-mono text-sm font-bold tracking-wider mb-3">HONEST</div>
            <h3 className="text-xl font-bold text-slate-900 mb-3">We say what it does</h3>
            <p className="text-slate-600">Plain descriptions of what each tool does and who it's for — no overclaiming.</p>
          </div>
        </div>
      </section>

      {/* Our brands */}
      <section className="bg-slate-50 py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-slate-900 mb-12 text-center">Our brands</h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <a 
              href="https://uplatz.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-white p-8 rounded-2xl border border-slate-200 hover:shadow-lg transition-all hover:-translate-y-1"
            >
              <div className="text-2xl font-bold text-slate-900 mb-3">Uplatz</div>
              <p className="text-slate-600 mb-4">Our online technology education platform — 350+ technology courses, 1.5 million learners across 50+ countries.</p>
              <span className="text-indigo-600 font-semibold inline-flex items-center gap-2 group">
                Visit uplatz.com 
                <span className="group-hover:translate-x-1 transition-transform">→</span>
              </span>
            </a>
            <a 
              href="https://uplatz.ai" 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-white p-8 rounded-2xl border border-slate-200 hover:shadow-lg transition-all hover:-translate-y-1"
            >
              <div className="text-2xl font-bold text-slate-900 mb-3">Uplatz<span className="text-indigo-600">.ai</span></div>
              <p className="text-slate-600 mb-4">Our AI arm — production-ready AI tools and custom-built solutions for teams and businesses.</p>
              <span className="text-indigo-600 font-semibold inline-flex items-center gap-2 group">
                Visit uplatz.ai 
                <span className="group-hover:translate-x-1 transition-transform">→</span>
              </span>
            </a>
          </div>
        </div>
      </section>

      {/* Company fact box */}
      <section className="py-20 max-w-7xl mx-auto px-6 lg:px-8">
        <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">The company behind it</h2>
        <div className="max-w-4xl mx-auto bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="p-6 border-b border-slate-200 flex flex-wrap items-center justify-between gap-4">
            <div className="text-xl font-bold text-slate-900">Uplatz.ai</div>
            <div className="flex items-center gap-2 text-emerald-700 font-mono text-sm font-medium">
              <span className="w-2 h-2 bg-emerald-600 rounded-full inline-block"></span>
              Established 2016 · UK & India
            </div>
          </div>

          <div className="p-6 border-b border-slate-200">
            <div className="text-indigo-600 font-mono text-xs font-bold tracking-wider mb-3">UNITED KINGDOM</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="text-slate-500 font-mono text-xs font-medium">Legal entity</div>
                <div className="text-slate-900 font-medium">Pazit Infotech Limited</div>
              </div>
              <div>
                <div className="text-slate-500 font-mono text-xs font-medium">Company no.</div>
                <div className="text-slate-900 font-medium">10049942</div>
              </div>
              <div className="md:col-span-2">
                <div className="text-slate-500 font-mono text-xs font-medium">Registered</div>
                <div className="text-slate-900 font-medium">England & Wales · incorporated 7 March 2016</div>
              </div>
              <div className="md:col-span-2">
                <div className="text-slate-500 font-mono text-xs font-medium">Registered office</div>
                <div className="text-slate-900 font-medium">63 Chelsea House, 599 Witan Gate, Milton Keynes, MK9 2BS</div>
              </div>
            </div>
          </div>

          <div className="p-6 border-b border-slate-200">
            <div className="text-indigo-600 font-mono text-xs font-bold tracking-wider mb-3">INDIA</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="text-slate-500 font-mono text-xs font-medium">Legal entity</div>
                <div className="text-slate-900 font-medium">Pazit Infotech Private Limited</div>
              </div>
              <div>
                <div className="text-slate-500 font-mono text-xs font-medium">CIN</div>
                <div className="text-slate-900 font-medium">U72200CT2016PTC002094</div>
              </div>
              <div>
                <div className="text-slate-500 font-mono text-xs font-medium">Incorporated</div>
                <div className="text-slate-900 font-medium">25 January 2016</div>
              </div>
              <div>
                <div className="text-slate-500 font-mono text-xs font-medium">Technology centre</div>
                <div className="text-slate-900 font-medium">Jaipur, India</div>
              </div>
            </div>
          </div>

          <div className="p-6 bg-slate-50 text-sm text-slate-600">
            <strong className="text-slate-900">Uplatz</strong> and <strong className="text-slate-900">Uplatz.ai</strong> are trading names of Pazit Infotech Limited (UK). Operations run from the UK and India, serving customers worldwide.
          </div>
        </div>
      </section>

      {/* Ecosystem cross-link */}
      <section className="bg-slate-900 text-white py-20 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -bottom-1/2 -left-1/4 w-[600px] h-[600px] rounded-full bg-gradient-to-tr from-indigo-500/20 to-purple-500/20 blur-3xl" />
        </div>
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <h3 className="text-3xl font-bold mb-4">One of many tools at Uplatz AI</h3>
          <p className="text-slate-300 text-lg mb-8 max-w-2xl mx-auto">
            Text2SQL sits alongside a growing suite of AI tools for founders, data teams, and specialists — plus custom builds for teams that need something off-the-shelf can't do.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a 
              href="https://uplatz.ai/#tools" 
              className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-8 py-3 rounded-xl transition-colors"
            >
              See all tools →
            </a>
            <a 
              href="https://uplatz.ai/#build" 
              className="bg-red-600 hover:bg-red-700 text-white font-semibold px-8 py-3 rounded-xl transition-colors"
            >
              Book a custom build →
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutUs;